import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import PDFReport from './PDFReport';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth/useAuth';

/**
 * Standalone PDF Report Preview Page
 * Opens in a new tab to display the full PDF report preview
 */
const PDFReportPage = () => {
  const { inspectionId } = useParams();
  const [searchParams] = useSearchParams();
  const includeDrafts = searchParams.get('drafts') === 'true';
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inspection, setInspection] = useState(null);
  const [defectReport, setDefectReport] = useState({ loading: false, error: null, data: null });
  const [photos, setPhotos] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [assetType, setAssetType] = useState(null);
  const [assetDetails, setAssetDetails] = useState(null);
  const [reportTitle, setReportTitle] = useState('Inspection Report');
  const [reportSummary, setReportSummary] = useState(null);
  const [reportDate, setReportDate] = useState(new Date());
  const [defectTypes, setDefectTypes] = useState([]);

  useEffect(() => {
    if (!inspectionId) {
      setError('No inspection ID provided');
      setLoading(false);
      return;
    }

    // Read user's defect exclusion list (if any) - written by InspectionDetailsModal
    // before opening this preview tab. We pull from localStorage by the nonce key,
    // then immediately delete the entry so it doesn't leak across sessions/tabs.
    const selectionKey = searchParams.get('selectionKey');
    let excludedIdsSet = new Set();
    if (selectionKey) {
      try {
        const raw = localStorage.getItem(`pdf-report-exclude-${selectionKey}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && Array.isArray(parsed.excluded)) {
            excludedIdsSet = new Set(parsed.excluded.map(String));
          }
          localStorage.removeItem(`pdf-report-exclude-${selectionKey}`);
        }
      } catch (e) {
        console.warn('[PDFReportPage] Could not read defect exclusion list:', e);
      }
    }

    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Fetch inspection details
        const inspectionResponse = await api.get(`/inspections/${inspectionId}`);
        const inspectionData = inspectionResponse.data;
        setInspection(inspectionData);

        // 2. Fetch asset details if available
        let assetData = null;
        let assetTypeName = null;
        if (inspectionData.asset_id) {
          try {
            const assetResponse = await api.get(`/assets/${inspectionData.asset_id}`);
            assetData = assetResponse.data;
            setAssetDetails(assetData);
            assetTypeName = assetData.type_name || assetData.asset_type_name || assetData.asset_type || null;
            setAssetType(assetTypeName);
          } catch (assetErr) {
            console.warn('Could not fetch asset details:', assetErr);
          }
        }
        // Fallback: inspection endpoint already joins asset_types and returns asset_type_name
        if (!assetTypeName && inspectionData.asset_type_name) {
          assetTypeName = inspectionData.asset_type_name;
          setAssetType(assetTypeName);
        }

        // 3. Fetch photos
        const photosResponse = await api.get(`/inspections/${inspectionId}/photos`);
        const allPhotos = photosResponse.data || [];
        setPhotos(allPhotos);

        // 4. Fetch defects
        const defectsResponse = await api.get(`/inspections/${inspectionId}/defects`);
        const defects = defectsResponse.data || [];

        // 5. Fetch draft findings if requested
        let draftFindings = [];
        if (includeDrafts) {
          try {
            const photosWithAnnotations = allPhotos.filter(p => p.has_annotation);
            const draftPromises = photosWithAnnotations.map(photo =>
              api.get(`/bounding-boxes`, {
                params: { photo_id: photo.id, status: 'draft' }
              })
            );
            const draftResponses = await Promise.all(draftPromises);
            draftResponses.forEach((response, index) => {
              if (Array.isArray(response.data) && response.data.length > 0) {
                const photo = photosWithAnnotations[index];
                const boxes = response.data.map(box => ({
                  id: `draft-${box.id}`,
                  defect_type_id: box.defect_type_id,
                  defect_type_name: box.defect_type_name || 'Unknown Defect',
                  description: box.notes || 'Draft finding from AI/Manual annotation',
                  status: 'draft',
                  severity_level_name: box.severity_level_name,
                  severity_level_id: box.severity_level_id,
                  created_at: box.created_at,
                  photo_path: photo.photo_path,
                  bounding_box: box,
                  is_draft: true
                }));
                draftFindings = [...draftFindings, ...boxes];
              }
            });
          } catch (draftErr) {
            console.warn('Could not fetch draft findings:', draftErr);
          }
        }

        // 6. Combine defects and fetch bounding boxes
        const allFindings = [...defects, ...draftFindings];
        const photoMap = new Map(allPhotos.map(p => [p.photo_path, p]));

        const defectsWithBoundingBoxes = await Promise.all(
          allFindings.map(async (item) => {
            if (item.is_draft) return item;
            return api.get(`/defects/${item.id}/bounding-box`)
              .then(bboxResponse => ({ ...item, bounding_box: bboxResponse.data }))
              .catch(() => ({ ...item, bounding_box: null }));
          })
        );

        // 7. Enrich defects with photo data
        // Apply the user's exclusion list (from InspectionDetailsModal) BEFORE enrichment so
        // photo aggregation, defect type counts, and totals all reflect the filtered view.
        const filteredFindings = excludedIdsSet.size > 0
          ? defectsWithBoundingBoxes.filter(d => !excludedIdsSet.has(String(d.id)))
          : defectsWithBoundingBoxes;

        const photoPathsWithDefects = new Set();
        const enrichedDefects = filteredFindings.map(defect => {
          if (defect.photo_path && photoMap.has(defect.photo_path)) {
            const photo = photoMap.get(defect.photo_path);
            photoPathsWithDefects.add(defect.photo_path);
            return {
              ...defect,
              original_image_path: photo.photo_path,
              image_width: photo.image_width,
              image_height: photo.image_height,
            };
          }
          return defect;
        });

        const photosWithDefects = allPhotos.filter(photo => photoPathsWithDefects.has(photo.photo_path));

        // 8. Calculate defect types
        const defectTypesMap = {};
        enrichedDefects.forEach(defect => {
          if (defect.defect_type_id && defect.defect_type_name) {
            if (!defectTypesMap[defect.defect_type_id]) {
              defectTypesMap[defect.defect_type_id] = {
                defect_type_id: defect.defect_type_id,
                defect_type_name: defect.defect_type_name,
                count: 0
              };
            }
            defectTypesMap[defect.defect_type_id].count++;
          }
        });
        const calculatedDefectTypes = Object.values(defectTypesMap);
        setDefectTypes(calculatedDefectTypes);

        // 9. Set defect report data
        setDefectReport({
          loading: false,
          error: null,
          data: {
            total: enrichedDefects.length,
            defects: enrichedDefects,
            defectTypes: calculatedDefectTypes,
            photosWithDefects: photosWithDefects,
            photosWithDefectsCount: photosWithDefects.length,
            isDraftPreview: includeDrafts
          }
        });

        // 10. Fetch severity levels
        try {
          const severityResponse = await api.get('/severity-levels');
          setSeverityLevels(severityResponse.data || []);
        } catch (sevErr) {
          console.warn('Could not fetch severity levels:', sevErr);
        }

        // 11. Generate report title based on asset type
        const generateTitle = () => {
          const draftSuffix = includeDrafts ? ' (Draft Findings)' : '';
          if (!assetTypeName) return `Inspection Report${draftSuffix}`;

          const typeLC = assetTypeName.toLowerCase();
          if (typeLC === 'bridge') return `Bridge Inspection Report${draftSuffix}`;
          if (typeLC === 'drainage') return `Drainage Inspection Report${draftSuffix}`;
          if (typeLC === 'road') return `Road Inspection Report${draftSuffix}`;
          if (typeLC === 'tunnel') return `Tunnel Inspection Report${draftSuffix}`;
          if (typeLC === 'culvert') return `Culvert Inspection Report${draftSuffix}`;
          if (typeLC === 'retaining wall') return `Retaining Wall Inspection Report${draftSuffix}`;
          if (typeLC === 'slope') return `Slope Inspection Report${draftSuffix}`;

          const capitalizedType = assetTypeName.charAt(0).toUpperCase() + assetTypeName.slice(1);
          return `${capitalizedType} Inspection Report${draftSuffix}`;
        };
        setReportTitle(generateTitle());

        // 12. Build report summary
        const inspectionDateStr = inspectionData.inspection_date || inspectionData.date;
        const inspectionDate = inspectionDateStr ? new Date(inspectionDateStr) : new Date();

        setReportSummary({
          network: { name: assetData?.network_name || 'N/A' },
          customField: {
            label: 'Asset ID',
            value: assetData?.code || 'N/A'
          },
          assetRating: assetData?.rating_name || 'Not Rated',
          inspectionType: inspectionData.inspection_type_name || 'Standard Inspection',
          inspectionCycle: inspectionData.cycle || null,
          inspectionConditionRating: inspectionData.condition_rating_name || 'Not Rated',
          startDate: inspectionDate,
          endDate: inspectionDate,
          selectAllDefects: true,
          selectedDefectTypes: calculatedDefectTypes,
          totalDefectTypes: calculatedDefectTypes.length,
          generatedBy: user?.fullName || user?.full_name || user?.username || 'System User'
        });

        // Set report date from URL param or default to today
        const dateParam = searchParams.get('date');
        if (dateParam) {
          setReportDate(new Date(dateParam));
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading report data:', err);
        setError(err.message || 'Failed to load report data');
        setLoading(false);
      }
    };

    fetchAllData();
  }, [inspectionId, includeDrafts, searchParams]);

  // Update document title
  useEffect(() => {
    if (inspection) {
      document.title = `${reportTitle} - Preview`;
    }
    return () => {
      document.title = 'GeoAI-RR';
    };
  }, [inspection, reportTitle]);

  if (loading) {
    return (
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        bgcolor: '#f5f5f5',
      }}>
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          Loading report preview...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3,
        bgcolor: '#f5f5f5',
      }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>Error Loading Report</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      // Claim all of MainContainer's vertical space (MainContainer is a flex column).
      // No display:flex here - PDFReport in fullPage mode uses block layout, and we don't
      // want a flex parent breaking the sticky bar inside it.
      flex: 1,
      minHeight: 0,
      bgcolor: '#f5f5f5',
    }}>
      <PDFReport
        inspection={inspection}
        defectReport={defectReport}
        photos={photos}
        severityLevels={severityLevels}
        assetType={assetType}
        reportTitle={reportTitle}
        reportSummary={reportSummary}
        reportDate={reportDate}
        fullPage
      />
    </Box>
  );
};

export default PDFReportPage;
